import Quill from 'quill'

const Parchment = Quill.import('parchment')

const MENU_ITEMS = [
  { action: 'insert-row-above', label: 'Inserir linha acima' },
  { action: 'insert-row-below', label: 'Inserir linha abaixo' },
  { action: 'insert-col-left', label: 'Inserir coluna à esquerda' },
  { action: 'insert-col-right', label: 'Inserir coluna à direita' },
  { separator: true },
  { action: 'delete-row', label: 'Remover linha', disabledWhen: (t) => t.table.children.length <= 1 },
  { action: 'delete-col', label: 'Remover coluna', disabledWhen: (t) => t.row.children.length <= 1 },
  { action: 'delete-table', label: 'Remover tabela' }
]

function randomId () {
  return Math.random().toString(36).slice(2)
}

// Localiza célula/linha/tabela/índice da coluna a partir de um blot de célula.
// Segue o padrão do TableTrick do quill-table (blots: td -> tr -> table).
function resolveFromCell (cell) {
  const row = cell.parent
  const table = row ? row.parent : null
  if (!row || !table || row.statics.blotName !== 'tr' || table.statics.blotName !== 'table') return null
  return {
    cell,
    row,
    table,
    colIndex: linkedListIndexOf(row.children, cell)
  }
}

function linkedListIndexOf (list, blot) {
  let index = 0
  let result = -1
  list.forEach(function (child) {
    if (result === -1 && child === blot) result = index
    index++
  })
  return result
}

function cellAt (row, colIndex) {
  let result = null
  let i = 0
  row.children.forEach(function (cell) {
    if (i === colIndex) result = cell
    i++
  })
  return result
}

function createCell (tableId, rowId) {
  const cellId = randomId()
  const td = Parchment.create('td', tableId + '|' + rowId + '|' + cellId)
  const p = Parchment.create('block')
  td.appendChild(p)
  const br = Parchment.create('break')
  p.appendChild(br)
  return td
}

// Mesmo padrão do append-row do TableTrick: casca clonada + células novas
function buildRow (table, refRow) {
  const tableId = table.domNode.getAttribute('table_id')
  const rowId = randomId()
  const row = refRow.clone()
  row.domNode.setAttribute('row_id', rowId)
  const colCount = refRow.children.length
  for (let i = 0; i < colCount; i++) {
    row.appendChild(createCell(tableId, rowId))
  }
  return row
}

// Todas as operações usam apenas APIs verificadas do Parchment 1.1.4
// (insertBefore, appendChild, remove, forEach) para manter o undo/redo
// funcionando via MutationObserver -> SCROLL_UPDATE -> TEXT_CHANGE.
const operations = {
  'insert-row-above' (target) {
    target.table.insertBefore(buildRow(target.table, target.row), target.row)
  },
  'insert-row-below' (target) {
    const newRow = buildRow(target.table, target.row)
    target.table.insertBefore(newRow, target.row.next)
  },
  'insert-col-left' (target) {
    const tableId = target.table.domNode.getAttribute('table_id')
    target.table.children.forEach(function (tr) {
      const refCell = cellAt(tr, target.colIndex)
      if (!refCell) return
      const rowId = tr.domNode.getAttribute('row_id')
      tr.insertBefore(createCell(tableId, rowId), refCell)
    })
  },
  'insert-col-right' (target) {
    const tableId = target.table.domNode.getAttribute('table_id')
    target.table.children.forEach(function (tr) {
      const refCell = cellAt(tr, target.colIndex)
      if (!refCell) return
      const rowId = tr.domNode.getAttribute('row_id')
      tr.insertBefore(createCell(tableId, rowId), refCell.next)
    })
  },
  'delete-row' (target) {
    if (target.table.children.length <= 1) return null
    const nextRow = target.row.next || target.row.prev
    const focusTarget = nextRow ? cellAt(nextRow, target.colIndex) : null
    target.row.remove()
    return focusTarget
  },
  'delete-col' (target) {
    if (target.row.children.length <= 1) return null
    const isLastColumn = target.colIndex >= target.row.children.length - 1
    target.table.children.forEach(function (tr) {
      const cell = cellAt(tr, target.colIndex)
      if (cell) cell.remove()
    })
    return cellAt(target.row, isLastColumn ? target.colIndex - 1 : target.colIndex)
  },
  'delete-table' (target, quill) {
    // Tabela como conteúdo único do editor: insere um bloco vazio antes
    // da remoção para o editor não ficar sem linha alguma
    if (target.table.prev == null && target.table.next == null) {
      quill.scroll.insertBefore(Parchment.create('block'), target.table)
    }
    const index = quill.getIndex(target.table)
    target.table.remove()
    return { index }
  }
}

class QuillTableMenu {
  constructor (quill) {
    this.quill = quill
    this.menu = null
    this.target = null
    this.onContextMenuBound = this.onContextMenu.bind(this)
    this.onDocumentClickBound = this.onDocumentClick.bind(this)
    this.onKeyDownBound = this.onKeyDown.bind(this)
    // Listener no root do editor: morre junto com o DOM do componente
    // (Quill 1.3.7 não possui hook destroy para módulos)
    quill.root.addEventListener('contextmenu', this.onContextMenuBound)
    // Envelopa o handler 'table' da toolbar (inserir tabela, append-row,
    // append-col) com snapshots de undo — o TableTrick original é chamado
    // por baixo (função estática, sem dependência de this)
    const toolbar = quill.getModule('toolbar')
    if (toolbar && toolbar.handlers && typeof toolbar.handlers.table === 'function') {
      const originalHandler = toolbar.handlers.table
      toolbar.handlers.table = (value) => {
        this.runWithSnapshot(() => { originalHandler(value) })
      }
    }
  }

  destroy () {
    this.closeMenu()
    if (this.quill && this.quill.root) {
      this.quill.root.removeEventListener('contextmenu', this.onContextMenuBound)
    }
    this.quill = null
    this.target = null
  }

  onContextMenu (event) {
    // closest() não existe no IE <= 10; browserslist do projeto é "not ie <= 8",
    // mas o polyfill manual evita surpresas (ex.: WebView antigo)
    const target = event.target
    let tdNode = target && target.tagName === 'TD' ? target : null
    if (!tdNode && target && typeof target.closest === 'function') {
      tdNode = target.closest('td')
    }
    if (!tdNode) {
      this.closeMenu()
      return
    }
    const cellBlot = Quill.find(tdNode)
    if (!cellBlot || cellBlot.statics.blotName !== 'td') return
    const resolved = resolveFromCell(cellBlot)
    if (!resolved) return
    event.preventDefault()
    event.stopPropagation()
    this.target = resolved
    this.openMenu(event.clientX, event.clientY)
  }

  openMenu (x, y) {
    this.closeMenu()
    const menu = document.createElement('div')
    menu.classList.add('ql-table-menu')
    MENU_ITEMS.forEach(function (item) {
      if (item.separator) {
        const sep = document.createElement('div')
        sep.classList.add('ql-table-menu-separator')
        menu.appendChild(sep)
        return
      }
      const option = document.createElement('div')
      option.classList.add('ql-table-menu-item')
      option.textContent = item.label
      const disabled = item.disabledWhen ? item.disabledWhen(this.target) : false
      if (disabled) {
        option.classList.add('disabled')
      } else {
        option.addEventListener('click', () => {
          this.runAction(item.action)
          this.closeMenu()
        })
      }
      menu.appendChild(option)
    }, this)
    document.body.appendChild(menu)
    menu.style.left = Math.max(8, Math.min(x, window.innerWidth - menu.offsetWidth - 8)) + 'px'
    menu.style.top = Math.max(8, Math.min(y, window.innerHeight - menu.offsetHeight - 8)) + 'px'
    this.menu = menu
    // Listeners globais registrados apenas enquanto o menu está aberto,
    // evitando vazamento entre montagens dos formulários em modais/abas
    document.addEventListener('click', this.onDocumentClickBound)
    document.addEventListener('keydown', this.onKeyDownBound)
  }

  closeMenu () {
    if (this.menu) {
      this.menu.remove()
      this.menu = null
      this.target = null
    }
    document.removeEventListener('click', this.onDocumentClickBound)
    document.removeEventListener('keydown', this.onKeyDownBound)
  }

  onDocumentClick (event) {
    if (this.menu && !this.menu.contains(event.target)) this.closeMenu()
  }

  onKeyDown (event) {
    if (event.key === 'Escape' || event.key === 'Esc') this.closeMenu()
  }

  // Executa uma mutação de tabela com snapshot completo de DOM:
  // antes (markTableOperation) e depois (completeTableOperation) da
  // operação, restauráveis pelo TableAwareHistory em undo/redo.
  // Sem o TableAwareHistory, degrada para o comportamento anterior.
  runWithSnapshot (mutate, postUpdate) {
    const quill = this.quill
    const history = quill.getModule('history')
    const supportsSnap = !!(history && typeof history.markTableOperation === 'function')
    let token = null
    if (supportsSnap) {
      const sel = quill.getSelection()
      token = history.markTableOperation(quill.root.innerHTML, sel ? sel.index : null)
    } else if (quill.history) {
      quill.history.cutoff()
    }
    try {
      mutate(quill)
      // Flush síncrono do MutationObserver: registra o undo/redo e dispara
      // onEditorChange (Vuex + Vuelidate) como na digitação
      quill.update(Quill.sources.USER)
      if (postUpdate) postUpdate(quill)
      if (supportsSnap) {
        const selAfter = quill.getSelection()
        history.completeTableOperation(token, quill.root.innerHTML, selAfter ? selAfter.index : null)
      }
    } finally {
      if (supportsSnap && typeof history.abortTableOperation === 'function') {
        history.abortTableOperation(token)
      }
    }
  }

  runAction (action) {
    const operation = operations[action]
    const target = this.target
    if (!operation || !target || !target.cell || !target.cell.domNode || !this.quill.root.contains(target.cell.domNode)) {
      this.closeMenu()
      return
    }
    let focus = null
    this.runWithSnapshot(
      (quill) => {
        // O menu opera sempre sobre a célula clicada (o clique direito
        // nem sempre move a seleção do editor)
        focus = operation(target, quill)
      },
      (quill) => {
        if (!focus) return
        if (focus.domNode) {
          focusCell(quill, focus)
        } else if (typeof focus.index === 'number') {
          const maxIndex = Math.max(0, quill.getLength() - 1)
          quill.setSelection(Math.max(0, Math.min(focus.index, maxIndex)))
        }
      }
    )
  }
}

function focusCell (quill, cell) {
  if (!cell || !cell.domNode || !cell.domNode.isConnected) return
  const index = quill.getIndex(cell)
  if (index == null || index < 0) return
  quill.setSelection(Math.max(0, index))
}

export default QuillTableMenu
